// components/QuarterlyMailPanel/QuarterlyMailPanel.jsx
// ============================================================
// Drop anywhere in your admin dashboard.
// Shows last sent date, next send date, eligible user count,
// and lets admin manually trigger the mail or change next date.
// ============================================================

import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import "./QuarterlyMailPanel.css";

const API_BASE = "https://api.assetview.warrantyindia.com/api";

function fmtDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function fmtDateTime(d) {
  if (!d) return null;
  return new Date(d).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function daysUntil(d) {
  if (!d) return null;
  const diff = new Date(d) - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function Countdown({ nextSendAt }) {
  const days = daysUntil(nextSendAt);
  if (days == null) return null;
  if (days < 0) return <span className="qmp-badge qmp-badge-overdue">Overdue by {Math.abs(days)}d</span>;
  if (days === 0) return <span className="qmp-badge qmp-badge-today">Sending Today</span>;
  return <span className="qmp-badge qmp-badge-upcoming">{days} day{days !== 1 ? "s" : ""} away</span>;
}

const statusConfig = {
  sent:          { label: "Sent",          color: "#16a34a", bg: "#dcfce7", icon: "✓" },
  partial:       { label: "Partial",       color: "#ca8a04", bg: "#fef9c3", icon: "⚠" },
  failed:        { label: "Failed",        color: "#dc2626", bg: "#fee2e2", icon: "✗" },
  pending:       { label: "Pending",       color: "#2563eb", bg: "#dbeafe", icon: "●" },
  not_configured:{ label: "Not Configured",color: "#6b7280", bg: "#f3f4f6", icon: "—" },
};

export default function QuarterlyMailPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [triggerMsg, setTriggerMsg] = useState("");
  const [showSetDate, setShowSetDate] = useState(false);
  const [newDate, setNewDate] = useState("");
  const [savingDate, setSavingDate] = useState(false);
  const [error, setError] = useState("");

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API_BASE}/admin/mail-schedule`);
      if (res.data?.ok) setData(res.data.data);
      else setError(res.data?.error || "Failed to load");
    } catch (e) {
      setError("Failed to load mail schedule");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    // auto-refresh every 30s
    const t = setInterval(fetchStatus, 30000);
    return () => clearInterval(t);
  }, [fetchStatus]);

  const handleTrigger = async () => {
    if (!window.confirm("Send quarterly reminder emails to all regional managers right now?")) return;
    setTriggering(true);
    setTriggerMsg("");
    try {
      const res = await axios.post(`${API_BASE}/admin/mail-schedule/trigger`);
      setTriggerMsg(res.data?.message || "Triggered!");
      // Refresh after 3s to show updated status
      setTimeout(fetchStatus, 3000);
    } catch (e) {
      setTriggerMsg("Failed to trigger. Check server logs.");
    } finally {
      setTriggering(false);
    }
  };

  const handleSetDate = async () => {
    if (!newDate) return;
    setSavingDate(true);
    try {
      await axios.post(`${API_BASE}/admin/mail-schedule/set-next`, {
        next_send_at: newDate,
      });
      setShowSetDate(false);
      setNewDate("");
      fetchStatus();
    } catch (e) {
      alert("Failed to update date");
    } finally {
      setSavingDate(false);
    }
  };

  const status = data ? (statusConfig[data.last_status] || statusConfig.pending) : null;

  return (
    <div className="qmp-root">
      <div className="qmp-header">
        <div className="qmp-header-left">
          <span className="qmp-icon">📬</span>
          <div>
            <div className="qmp-title">Quarterly Asset Reminder</div>
            <div className="qmp-subtitle">Auto email to all Regional Managers every 3 months</div>
          </div>
        </div>
        <button className="qmp-refresh-btn" onClick={fetchStatus} disabled={loading} title="Refresh">
          <span className={loading ? "qmp-spin" : ""}>↻</span>
        </button>
      </div>

      {error && <div className="qmp-error">{error}</div>}

      {loading && !data ? (
        <div className="qmp-skeleton-wrap">
          <div className="qmp-skeleton" />
          <div className="qmp-skeleton qmp-skeleton-short" />
        </div>
      ) : data ? (
        <>
          {/* Status badge */}
          {data.last_status && (
            <div
              className="qmp-status-row"
              style={{ background: status.bg, color: status.color }}
            >
              <span className="qmp-status-icon">{status.icon}</span>
              <span>
                Last run: <strong>{status.label}</strong>
                {data.last_count > 0 && ` — ${data.last_count} email${data.last_count !== 1 ? "s" : ""} sent`}
              </span>
              {data.last_error && (
                <span className="qmp-status-err" title={data.last_error}>⚠ Error</span>
              )}
            </div>
          )}

          {/* Date timeline cards */}
          <div className="qmp-cards">
            <div className="qmp-card qmp-card-last">
              <div className="qmp-card-label">Last Sent</div>
              <div className="qmp-card-date">
                {data.last_sent_at ? fmtDate(data.last_sent_at) : "—"}
              </div>
              {data.last_sent_at && (
                <div className="qmp-card-time">{fmtDateTime(data.last_sent_at)}</div>
              )}
              {!data.last_sent_at && (
                <div className="qmp-card-never">Never sent yet</div>
              )}
            </div>

            <div className="qmp-card-arrow">→</div>

            <div className="qmp-card qmp-card-next">
              <div className="qmp-card-label">
                Next Send
                <Countdown nextSendAt={data.next_send_at} />
              </div>
              <div className="qmp-card-date">
                {data.next_send_at ? fmtDate(data.next_send_at) : "—"}
              </div>
              {data.next_send_at && (
                <div className="qmp-card-time">{fmtDateTime(data.next_send_at)}</div>
              )}
            </div>
          </div>

          {/* Users info */}
          <div className="qmp-meta-row">
            <span className="qmp-meta-item">
              👥 <strong>{data.eligible_users}</strong> Regional Managers will receive emails
            </span>
            <span className="qmp-meta-item">
              🗓 Every <strong>{data.interval_months} months</strong>
            </span>
          </div>

          {/* Action buttons */}
          <div className="qmp-actions">
            <button
              className="qmp-btn-trigger"
              onClick={handleTrigger}
              disabled={triggering}
            >
              {triggering ? "Sending…" : "📤 Send Now"}
            </button>
            <button
              className="qmp-btn-secondary"
              onClick={() => setShowSetDate(!showSetDate)}
            >
              📅 Change Next Date
            </button>
          </div>

          {triggerMsg && (
            <div className="qmp-trigger-msg">{triggerMsg}</div>
          )}

          {/* Set date form */}
          {showSetDate && (
            <div className="qmp-setdate-form">
              <label className="qmp-setdate-label">Set Next Send Date & Time</label>
              <div className="qmp-setdate-row">
                <input
                  type="datetime-local"
                  className="qmp-setdate-input"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                />
                <button
                  className="qmp-btn-save"
                  onClick={handleSetDate}
                  disabled={savingDate || !newDate}
                >
                  {savingDate ? "Saving…" : "Save"}
                </button>
                <button
                  className="qmp-btn-cancel"
                  onClick={() => { setShowSetDate(false); setNewDate(""); }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}